import React, { useEffect, useState, useMemo } from 'react'
import { Box, Tabs, Tab, Divider, Typography } from '@mui/material'
import { getDistrictWiseWorkerCounts, getDistrictWiseWORKerType, getAllStates } from '../../backend/backendApis'
import HttpService from '../../backend/requestApis'
import Loader from '../Loader'
import { headingBlue, pureWhite } from '../../assets/colors'
import CustomDropDown from '../CustomDropDown'
import DistrictTopWidgets from './DistrictTopWidgets'
import StateStatsTable from './StateStatsTable'
import DistrictStatsTable from './DistrictStatsTable'

const ChaptersManagement = () => {
  const [tabValue, setTabValue] = useState(0)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [isStatsLoading, setIsStatsLoading] = useState(false)

  // District Stats State
  const [statsRawData, setStatsRawData] = useState([])
  const [statsPage, setStatsPage] = useState(1)
  const [statsOffset, setStatsOffset] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [selectedState, setSelectedState] = useState('All States')
  const [stateList, setStateList] = useState([{ name: 'All States' }])



  // Memoized District Top Widgets
  const topWidgetData = useMemo(() => {
    if (!statsRawData || statsRawData.length === 0) {
      return { totalDistricts: 0, districtsWithMaleWorkers: 0, districtsWithFemaleWorkers: 0, districtHeads: 0, deputyDistrictHeads: 0 }
    }

    let maleWorkersDistricts = 0
    let femaleWorkersDistricts = 0
    let dhCount = 0
    let ddhCount = 0

    statsRawData.forEach(row => {
      const maleCnt = includeInactive
        ? Number(row.maleWorkersCountTotal ?? row.maleWorkersCount ?? 0)
        : Number(row.maleWorkersCountActive ?? 0)
      const femaleCnt = includeInactive
        ? Number(row.femaleWorkersCountTotal ?? row.femaleWorkersCount ?? 0)
        : Number(row.femaleWorkersCountActive ?? 0)

      if (maleCnt > 0) maleWorkersDistricts++
      if (femaleCnt > 0) femaleWorkersDistricts++

      const dhName = row.districtHead || row.districtHeadMale
      const ddhName = row.deputyDistrictHead || row.districtHeadFemale

      if (dhName && String(dhName).trim() !== '' && String(dhName).trim() !== '-') {
        dhCount++
      }
      if (ddhName && String(ddhName).trim() !== '' && String(ddhName).trim() !== '-') {
        ddhCount++
      }
    })

    return {
      totalDistricts: statsRawData.length,
      districtsWithMaleWorkers: maleWorkersDistricts,
      districtsWithFemaleWorkers: femaleWorkersDistricts,
      districtHeads: dhCount,
      deputyDistrictHeads: ddhCount
    }
  }, [statsRawData, includeInactive])

  const fetchCombinedStats = (stFilter = null) => {
    setIsStatsLoading(true)

    let countsOptions = {
      url: getDistrictWiseWorkerCounts,
      method: 'GET',
      params: {
        ...(stFilter && stFilter !== 'All States' && { state: stFilter })
      },
      headers: {
        Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
      }
    }

    let typeOptions = {
      url: getDistrictWiseWORKerType,
      method: 'GET',
      params: {
        statTileValue: 'total',
        offset: 10000
      },
      headers: {
        Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
      }
    }

    Promise.all([
      HttpService(countsOptions).catch(() => null),
      HttpService(typeOptions).catch(() => null)
    ]).then(([countsResp, typeResp]) => {
      let respObj = countsResp?.data
      let rawList = []
      if (Array.isArray(respObj?.data)) {
        rawList = respObj.data
      } else if (Array.isArray(respObj)) {
        rawList = respObj
      } else if (respObj?.data && typeof respObj.data === 'object') {
        rawList = Array.isArray(respObj.data.data) ? respObj.data.data : (Array.isArray(respObj.data.records) ? respObj.data.records : [])
      }

      let typeData = typeResp?.data?.data?.data || typeResp?.data?.data || typeResp?.data || []
      const typeMap = {}

      if (Array.isArray(typeData)) {
        typeData.forEach(item => {
          let distStr = item.district || ''
          let distName = distStr.split('-')[0].trim().toLowerCase()
          let genderStr = (item.gender || '').trim().toLowerCase()
          let genderKey = (genderStr === 'm' || genderStr === 'male') ? 'male' : 'female'

          let activeCnt = 0
          let totalCnt = 0

          if (Array.isArray(item.types)) {
            item.types.forEach(tObj => {
              const countVal = Number(tObj.count || 0)
              totalCnt += countVal
              const tName = String(tObj.type || '').trim().toLowerCase()
              if (tName !== 'inactive') {
                activeCnt += countVal
              }
            })
          } else {
            totalCnt = Number(item.totalLeads || 0)
            activeCnt = totalCnt
          }

          const mapKey = `${distName}_${genderKey}`
          if (!typeMap[mapKey]) {
            typeMap[mapKey] = { activeCount: 0, totalCount: 0 }
          }
          typeMap[mapKey].activeCount += activeCnt
          typeMap[mapKey].totalCount += totalCnt
        })
      }

      const enhancedList = rawList.map(dist => {
        const dName = (dist.district || '').trim().toLowerCase()
        const maleKey = `${dName}_male`
        const femaleKey = `${dName}_female`

        const maleTotal = Number(dist.maleWorkersCount ?? dist.workerCountMale ?? typeMap[maleKey]?.totalCount ?? 0)
        const femaleTotal = Number(dist.femaleWorkersCount ?? dist.workerCountFemale ?? typeMap[femaleKey]?.totalCount ?? 0)

        const maleActive = typeMap[maleKey] ? typeMap[maleKey].activeCount : maleTotal
        const femaleActive = typeMap[femaleKey] ? typeMap[femaleKey].activeCount : femaleTotal

        return {
          ...dist,
          maleWorkersCountTotal: maleTotal,
          femaleWorkersCountTotal: femaleTotal,
          totalWorkersCountTotal: maleTotal + femaleTotal,

          maleWorkersCountActive: maleActive,
          femaleWorkersCountActive: femaleActive,
          totalWorkersCountActive: maleActive + femaleActive,

          maleWorkersCount: maleTotal,
          femaleWorkersCount: femaleTotal,
          totalWorkersCount: maleTotal + femaleTotal
        }
      })

      setStatsRawData(enhancedList)
      setIsStatsLoading(false)
    }).catch(err => {
      console.log('Error in fetching combined stats: ', err)
      setIsStatsLoading(false)
    })
  }

  useEffect(() => {
    let options = {
      url: getAllStates,
      method: 'GET',
      headers: {
        Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
      }
    }
    HttpService(options).then((resp) => {
      let fetched = resp?.data?.data || []
      if (Array.isArray(fetched)) {
        let formatted = fetched.map(item => (typeof item === 'string' ? { name: item } : { name: item.name || item.state || item.stateName || item }))
        setStateList([{ name: 'All States' }, ...formatted])
      }
    }).catch(err => {
      console.log('Error in fetching states list: ', err)
    })
  }, [])

  useEffect(() => {
    if (tabValue === 1) {
      fetchCombinedStats(selectedState)
    }
  }, [tabValue, selectedState])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // District Stats Memo
  const filteredRawData = useMemo(() => {
    if (!statsRawData || statsRawData.length === 0) return []
    if (!searchText || searchText.trim() === '') return statsRawData
    const search = searchText.trim().toLowerCase()
    return statsRawData.filter(row => (
      (row.district && row.district.toLowerCase().includes(search)) ||
      (row.state && row.state.toLowerCase().includes(search)) ||
      (row.stateHead && row.stateHead.toLowerCase().includes(search)) ||
      (row.deputyStateHead && row.deputyStateHead.toLowerCase().includes(search)) ||
      (row.districtHead && row.districtHead.toLowerCase().includes(search)) ||
      (row.deputyDistrictHead && row.deputyDistrictHead.toLowerCase().includes(search))
    ))
  }, [statsRawData, searchText])

  const displayStatsData = useMemo(() => {
    if (!filteredRawData || filteredRawData.length === 0) return []
    const sorted = [...filteredRawData].sort((a, b) => {
      const countA = includeInactive
        ? Number(a.totalWorkersCountTotal ?? a.totalWorkersCount ?? (Number(a.maleWorkersCount || 0) + Number(a.femaleWorkersCount || 0)))
        : Number(a.totalWorkersCountActive ?? (Number(a.maleWorkersCountActive || 0) + Number(a.femaleWorkersCountActive || 0)))
      const countB = includeInactive
        ? Number(b.totalWorkersCountTotal ?? b.totalWorkersCount ?? (Number(b.maleWorkersCount || 0) + Number(b.femaleWorkersCount || 0)))
        : Number(b.totalWorkersCountActive ?? (Number(b.maleWorkersCountActive || 0) + Number(b.femaleWorkersCountActive || 0)))
      return countB - countA
    })
    const start = (statsPage - 1) * statsOffset
    return sorted.slice(start, start + statsOffset)
  }, [filteredRawData, statsPage, statsOffset, includeInactive])

  const totalRecs = filteredRawData.length

  const isAdmin = localStorage.getItem('designation') === 'admin'

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
          Access Restricted: Chapters Management is available only for Administrators.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: 'calc(100vh - 50px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {isStatsLoading && <Loader />}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, backgroundColor: pureWhite, flexShrink: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: headingBlue, height: '3px', borderRadius: '3px 3px 0 0' } }}
          sx={{
            minHeight: '2.8rem',
            '& .MuiTab-root': {
              fontSize: '0.95rem',
              textTransform: 'none',
              fontWeight: 600,
              color: '#888',
              padding: '0.4rem 1.5rem',
              minHeight: '2.8rem',
              transition: '0.3s ease',
            },
            '& .MuiTab-root:hover': {
              color: headingBlue,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
            '& .Mui-selected': { color: `${headingBlue} !important` }
          }}
        >
          <Tab label="State Stats" />
          <Tab label="District Stats" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={{ padding: '0.8rem 1rem 0.2rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <StateStatsTable stateList={stateList} />
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={{ padding: '0.8rem 1rem 0.2rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Top State Filter Dropdown above Top Widget */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', mb: '0.8rem', width: '100%' }}>
            <CustomDropDown
              key='topStateFilter'
              width="14rem"
              list={stateList}
              value={selectedState}
              placeHolder='All States'
              setFunc={(val) => {
                setSelectedState(val)
                setStatsPage(1)
              }}
              regular={true}
            />
            <Divider sx={{ width: "100%", mt: "0.2rem" }}></Divider>
          </Box>


          <DistrictTopWidgets data={topWidgetData} />
          <DistrictStatsTable
            displayData={displayStatsData}
            totalRecs={totalRecs}
            page={statsPage}
            setPage={setStatsPage}
            offset={statsOffset}
            setOffset={setStatsOffset}
            searchText={searchText}
            setSearchText={setSearchText}
            includeInactive={includeInactive}
            setIncludeInactive={setIncludeInactive}
            isLoading={isStatsLoading}
          />
        </Box>
      )}
    </Box>
  )
}

export default ChaptersManagement